import React, { useEffect, useState, useCallback } from "react"
import Layout from "@/components/layout/Layout"
import MainHeading from "@/components/common/MainHeading"
import { Download, Eye, MoreHorizontal } from "lucide-react"
import CommonTable, { type TableColumn } from "@/components/common/CommonTable"
import { Button } from "@/components/ui/button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useNavigate } from "react-router-dom"
import { RoutesLink } from "@/utility/RouteLinks"
import { AttendanceService } from "@/services/Attendance/AttendanceService"
import { ThrowError } from "@/api/callApi"
import type { IAttendanceSummaryUser, IMarkAttendancePayload } from "@/interface/attendance.interface"
import type { IPageInfo, IFilter } from "@/interface/common.interface"
import TablePagination from "@/components/common/TablePagination"
import CommonFilter from "@/components/common/CommonFilter"
import moment from "moment"
import { showToastNotification } from "@/utility/helper"
import { useAppFilters } from "@/hooks/useAppFilters"

const DailyTeamAttendance: React.FC = () => {
    const navigate = useNavigate();

    const [paginationData, setPaginationData] = useState<IPageInfo>({
        currentPage: 1,
        pageSize: 10,
        total: 0,
        totalPages: 0,
    });

    const {
        isHQ,
        effectiveBranchId,
        effectiveSiteId,
        filterValues,
        queryString: filterQueryString,
        filterSelectorsNode,
    } = useAppFilters({
        enabled: ['branch', 'site', 'department', 'designation', 'shift'],
        autoSelectFirst: { branch: true, site: true },
        onChange: () => {
            setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
        },
    });

    const [filter, setFilter] = useState<IFilter>({
        search: "",
        date: moment().format("YYYY-MM-DD"),
    });

    const handleSearchChange = (value: string) => {
        setFilter((prev) => {
            if (prev.search === value) return prev;
            return { ...prev, search: value };
        });
        setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    };

    const handleSingleDateChange = useCallback((date: string) => {
        setFilter((prev) => ({ ...prev, date: date }));
        setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    }, []);

    const [attendanceData, setAttendanceData] = useState<IAttendanceSummaryUser[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isExporting, setIsExporting] = useState<boolean>(false);

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const searchParam = filter.search
                ? `&search=${encodeURIComponent(filter.search)}`
                : "";

            const totalRecords = paginationData.pageSize || 1000;
            const params = `download=true&date=${filter.date}&pageNumber=${paginationData.currentPage}&pageSize=${totalRecords}${filterQueryString}${searchParam}`;

            const blob = await AttendanceService.downloadDailyAttendanceReport(params);

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `daily_attendance_${filter.date}.xlsx`
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsExporting(false);
        }
    };

    const handleGetDailyAttendance = async (
        page = paginationData.currentPage,
        limit = paginationData.pageSize
    ) => {
        // If Level 0 (HQ) and no branch/site selected yet, skip until loaded
        if (isHQ && !effectiveBranchId && !effectiveSiteId) return;

        setIsLoading(true);
        try {
            const searchParam = filter.search ? `&search=${encodeURIComponent(filter.search)}` : "";
            const params = `type=daily&date=${filter.date}&pageNumber=${page}&pageSize=${limit}${filterQueryString}${searchParam}`;
            const response = await AttendanceService.getDailyTeamAttendance(params);
            if (response?.status && response?.data) {
                setAttendanceData(response.data.users || []);
                setPaginationData((prev) => ({
                    ...prev,
                    total: response?.data?.pageInfo?.total || 0,
                    totalPages: response?.data?.pageInfo?.totalPages || 0,
                    currentPage: page,
                    pageSize: limit,
                }));
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdateStatus = async (item: IAttendanceSummaryUser, newStatus: string) => {
        try {
            const payload: IMarkAttendancePayload = {
                userId: item.user?.id,
                siteId: effectiveSiteId || "",
                date: filter.date,
                status: newStatus as any,
                isMarkedByHRM: true,
                markingAttendance: true
            };

            let response;
            if (item.attendanceId) {
                response = await AttendanceService.updateAttendance(item.attendanceId, payload);
            } else {
                response = await AttendanceService.markAttendance(payload);
            }

            if (response?.status) {
                showToastNotification("success", `Attendance status updated successfully.`);
                handleGetDailyAttendance(paginationData.currentPage, paginationData.pageSize);
            } else {
                showToastNotification("danger", response?.message || "Failed to update status");
            }
        } catch (error) {
            ThrowError(error);
        }
    };

    const handleChangePageSize = (value: number) => {
        setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
    };

    useEffect(() => {
        handleGetDailyAttendance(paginationData.currentPage, paginationData.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationData.currentPage, paginationData.pageSize, filter.date, filter.search, effectiveBranchId, effectiveSiteId, filterValues.departmentId, filterValues.designationId, filterValues.shiftId]);

    const getStatusColorClass = (status: string) => {
        switch (status?.toUpperCase()) {
            case "PRESENT":
                return "text-green-600 border-green-200 bg-green-50 focus:ring-green-100";
            case "ABSENT":
                return "text-red-600 border-red-200 bg-red-50 focus:ring-red-100";
            case "LEAVE":
                return "text-cyan-600 border-cyan-200 bg-cyan-50 focus:ring-cyan-100";
            case "PENDING":
            default:
                return "text-amber-600 border-amber-200 bg-amber-50 focus:ring-amber-100";
        }
    };

    const formatTime = (timeStr: string | null | undefined) => {
        if (!timeStr) return "—";
        if (timeStr.includes("T") || timeStr.includes("-")) {
            return moment.utc(timeStr).format("hh:mm A");
        }
        return moment(timeStr, "HH:mm:ss").format("hh:mm A");
    };

    const handleAttendanceLog = (item: IAttendanceSummaryUser) => {
        const id = item?.attendanceId;
        const user_id = item?.user?.id;
        if (!id || !user_id) {
            showToastNotification("danger", "Please mark attendance first.");
            return;
        }
        if (item?.attendanceType === "Leave" || item?.status === "LEAVE") {
            showToastNotification("danger", "Leave cannot be viewed.");
            return;
        }

        navigate(RoutesLink.attendanceLog, {
            state: {
                date: filter.date,
                userId: user_id,
                attendanceId: id,
            },
        });
    };

    const columns: TableColumn<IAttendanceSummaryUser>[] = [
        {
            key: 'sNo',
            header: 'S.No',
            render: (_item: IAttendanceSummaryUser, index: number) => (
                <span className="text-muted-foreground font-medium text-sm">{String(
                    (paginationData.currentPage - 1) * paginationData.pageSize + index + 1
                ).padStart(2, "0")}</span>
            )
        },
        {
            key: "empid",
            header: "Emp ID",
            render: (item) => (
                <span className="font-semibold text-slate-700">
                    {item.user?.employeeCode || "—"}
                </span>
            )
        },
        {
            key: "name",
            header: "Name",
            render: (item) => (
                <span onClick={() => navigate(`${RoutesLink.employeeDetail}/${item?.user?.id}`)} className="font-bold text-slate-900 cursor-pointer ">{item.user?.name || "—"}</span>
            )
        },
        {
            key: "department",
            header: "Department",
            render: (item) => <span>{item.department || "—"}</span>
        },
        {
            key: "designation",
            header: "Designation",
            render: (item) => <span>{item.designation || "—"}</span>
        },

        {
            key: "shift",
            header: "Shift",
            render: (item) => <span>{item.shift || "—"}</span>
        },
        {
            key: "status",
            header: "Status",
            render: (item) => (
                <select
                    value={item.status?.toUpperCase()}
                    onChange={async (e) => {
                        const newStatus = e.target.value;
                        await handleUpdateStatus(item, newStatus);
                    }}
                    className={`text-xs font-semibold px-3 py-1.5 border rounded-lg focus:outline-hidden focus:ring-2 cursor-pointer transition-colors ${getStatusColorClass(item.status || "PENDING")}`}
                >
                    <option value="PENDING" className="text-amber-600 bg-white">Pending</option>
                    <option value="PRESENT" className="text-green-600 bg-white">Present</option>
                    <option value="ABSENT" className="text-red-600 bg-white">Absent</option>
                    <option value="LEAVE" className="text-cyan-600 bg-white">On Leave</option>
                    <option value="WEEK_OFF" className="text-orange-600 bg-white">Week Off</option>

                </select>
            )
        },
        {
            key: "firstHalfCheckInTime",
            header: "First Half Check In",
            render: (item) => (
                <span className="text-slate-600 font-medium ">
                    {formatTime(item.firstHalfCheckInTime)}
                </span>
            )
        },
        {
            key: "firstHalfCheckOutTime",
            header: "First Half Check Out",
            render: (item) => (
                <span className="text-slate-600 font-medium">
                    {formatTime(item.firstHalfCheckOutTime)}
                </span>
            )
        },
        {
            key: "secondHalfCheckInTime",
            header: "Second Half Check In",
            render: (item) => (
                <span className="text-slate-600 font-medium">
                    {formatTime(item.secondHalfCheckInTime)}
                </span>
            )
        },
        {
            key: "secondHalfCheckOutTime",
            header: "Second Half Check Out",
            render: (item) => (
                <span className="text-slate-600 font-medium">
                    {formatTime(item.secondHalfCheckOutTime)}
                </span>
            )
        },

        {
            key: "actions",
            header: "Actions",
            render: (item) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-slate-100">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-xl shadow-lg border-slate-100">
                        <DropdownMenuItem
                            onClick={() => handleAttendanceLog(item)}
                            className="cursor-pointer"
                        >
                            <Eye className="h-4 w-4" />View Logs
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )
        }
    ];

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 flex-wrap">
                    <MainHeading title="Daily Team Attendance" isBackButtonVisible={true} />
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="outline"
                            className="bg-white"
                            onClick={handleExport}
                            disabled={isExporting}
                        >
                            <Download size={16} className="mr-2" />
                            {isExporting ? "Exporting..." : "Export"}
                        </Button>
                    </div>
                </div>

                <CommonFilter
                    searchValue={filter?.search || ""}
                    onSearchChange={handleSearchChange}
                    singleDate={filter.date || ""}
                    onSingleDateChange={handleSingleDateChange}
                    searchPlaceholder="Search employees..."
                    value={filter}
                    additionalFilters={filterSelectorsNode}
                />

                <CommonTable columns={columns} data={attendanceData} loading={isLoading} stickyColumns={3} />

                <TablePagination
                    paginationData={paginationData}
                    setPaginationData={setPaginationData}
                    handleChangePageSize={handleChangePageSize}
                    isLoading={isLoading}
                />
            </div>
        </Layout>
    );
};

export default DailyTeamAttendance;